import React, { useEffect, useState } from "react";
import { Spinner } from "../../../components";
import { useAdminPolicyInsights } from "../../../api/hooks/admin";
import { isoFromDateInput, MetricsMode } from "../shared/constants";
import { formatInt } from "../shared/formatters";
import AgencyPicker from "../UserTotals/AgencyPicker";
import CarriersChart from "./CarriersChart";
import CoverageLinesChart from "./CoverageLinesChart";
import LobSplitPie from "./LobSplitPie";

interface PolicyInsightsProps {
  mode: MetricsMode;
  startA: string;
  endA: string;
  startB: string;
  endB: string;
  includeTestAccounts: boolean;
  onFetchingChange?: (fetching: boolean) => void;
}

// Picking the window: the Policy Insights view is single-period only (no
// "compare" view yet). In compare mode we anchor on period B so the user sees
// "now" matching the rest of the page; in single mode use period A.
const _windowFor = (mode: MetricsMode, startA: string, endA: string, startB: string, endB: string) => {
  if (mode === "compare" && startB && endB) {
    return { start: startB, end: endB };
  }
  return { start: startA, end: endA };
};

const kpiCard: React.CSSProperties = {
  background: "#fff",
  border: "1px solid #e5e7eb",
  borderRadius: 8,
  padding: "16px 20px",
  flex: "1 1 0",
  minWidth: 180,
};

const kpiLabel: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  color: "#6b7280",
  letterSpacing: 0.4,
  textTransform: "uppercase",
};

const kpiValue: React.CSSProperties = {
  fontSize: 28,
  fontWeight: 700,
  marginTop: 6,
  color: "#1a1a1a",
};

const kpiSub: React.CSSProperties = {
  fontSize: 11,
  color: "#9ca3af",
  marginTop: 4,
};

const PolicyInsights: React.FC<PolicyInsightsProps> = ({
  mode,
  startA,
  endA,
  startB,
  endB,
  includeTestAccounts,
  onFetchingChange,
}) => {
  const [agencyId, setAgencyId] = useState<string | null>(null);
  const [agencyName, setAgencyName] = useState<string | null>(null);

  const { start, end } = _windowFor(mode, startA, endA, startB, endB);
  const startIso = start ? isoFromDateInput(start) : "";
  const endIso = end ? isoFromDateInput(end) : "";

  const q = useAdminPolicyInsights(
    startIso && endIso
      ? { startDate: startIso, endDate: endIso, agencyId, includeTestAccounts }
      : null
  );

  useEffect(() => {
    onFetchingChange?.(q.isFetching);
  }, [q.isFetching, onFetchingChange]);

  const data = q.data;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16, alignItems: "center", width: "100%" }}>
      <div style={{ width: "100%", maxWidth: 1200, display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
        <div style={{ fontSize: 12, color: "#6b7280" }}>
          Showing {startIso.slice(0, 10)} → {endIso.slice(0, 10)}{" "}
          {agencyId ? `· filtered to ${agencyName ?? "selected agency"}` : "· all agencies"}
        </div>
        {agencyId ? (
          <button
            onClick={() => {
              setAgencyId(null);
              setAgencyName(null);
            }}
            style={{
              padding: "6px 12px",
              fontSize: 12,
              borderRadius: 4,
              border: "1px solid #d1d5db",
              background: "#fff",
              cursor: "pointer",
            }}
          >
            Clear filter
          </button>
        ) : null}
      </div>

      <AgencyPicker
        selectedAgencyId={agencyId}
        onSelect={(id, name) => {
          setAgencyId(id);
          setAgencyName(name);
        }}
      />

      {q.isLoading || q.isPreviousData ? (
        <div style={{ padding: 40 }}>
          <Spinner />
        </div>
      ) : !data ? (
        <div style={{ padding: 40, color: "#6b7280" }}>No data yet.</div>
      ) : (
        <>
          {/* KPI strip: documents analyzed, data points extracted */}
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", width: "100%", maxWidth: 1200 }}>
            <div style={kpiCard}>
              <div style={kpiLabel}>Documents analyzed</div>
              <div style={kpiValue}>{formatInt(data.documents_analyzed)}</div>
              <div style={kpiSub}>Unique policy uploads in window</div>
            </div>
            <div style={kpiCard}>
              <div style={kpiLabel}>Data points extracted</div>
              <div style={kpiValue}>{formatInt(data.data_points_extracted)}</div>
              <div style={kpiSub}>Coverage entries pulled from policies</div>
            </div>
          </div>

          {/* Two-up: LOB pie + carriers leaderboard */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "minmax(280px, 1fr) minmax(280px, 1.6fr)",
              gap: 12,
              width: "100%",
              maxWidth: 1200,
            }}
          >
            <LobSplitPie split={data.lob_split} />
            <CarriersChart rows={data.carriers} />
          </div>

          {/* Coverage lines bar chart, full width */}
          <CoverageLinesChart rows={data.coverage_lines} />
        </>
      )}
    </div>
  );
};

export default PolicyInsights;
